//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import { noop } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { Icon } from '@components/atoms/icon/Icon';
import { debounce } from '@utils/debounce';

type Size = 'large' | 'small';

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  resetScrollDep: unknown;
  scrollDistance?: number | ((distanceContentShiftedLeft: number) => number);
  header?: React.ReactElement;
  emptyState?: React.ReactElement;
  overflowHorizontally?: number;
  onMouseEnter?: (event: React.MouseEvent) => void;
  onMouseLeave?: (event: React.MouseEvent) => void;
  alwaysShowArrows?: boolean;
  childSize?: Size;
}

export const ARROW_WIDTH_SMALL = 30;
export const ARROW_WIDTH_LARGE = 80;

/**
 * The HorizontalScrolling organism is used to contain a component
 * that is often too wide to display in a given context. When the
 * child component is wider than the size of this organism, we
 * allow the user to pan left and right by clicking on arrows that
 * only appear when necessary and only on hover.
 *
 * You must provide a resetScrollDep prop, which is used to determine when
 * to reset the scroll position (e.g. when the data used to build
 * the children prop is changed).
 *
 * By providing a `scrollDistance`, you set the number of pixels
 * we'll pan left or right when you click one of the arrows. When
 * this value is undefined, we scroll one full visible width. This
 * can be a function of the existing 'distanceContentShiftedLeft'
 * value if we need variable scroll distances.
 *
 * If a header is provided, we'll render it above the scrollable
 * section.
 *
 * We'll only render the emptyState if there are zero child nodes.
 *
 * If an overflowHorizontally prop is provided, the component will
 * widen as many pixels to the right. This allows the developer
 * to have the component reach the edges of a container that otherwise
 * has a margin, resulting in empty whitespace on the right side.
 */
const HorizontalScrolling = ({
  className,
  children,
  resetScrollDep,
  scrollDistance,
  header,
  emptyState,
  overflowHorizontally,
  onMouseEnter = noop,
  onMouseLeave = noop,
  alwaysShowArrows = false,
  childSize = 'small',
}: Props) => {
  // The diagram below illustrates how the two most important values you'll
  // find throughout this component's functions can be used to visualize what's
  // happening in the browser. The `distanceContentShiftedLeft` is manipulated by
  // the component to show/hide certain sections of its child, while the
  // `getRemainingContentSizeToRight()` is used to calculate the number of pixels
  // the child still extends beyond the bounds on this container.
  //
  //   distanceContentShiftedLeft                     getRemainingContentSizeToRight()
  //               ▽                                                 ▽
  // ..............................___________................................................
  // :           hidden           |  visible  |                    hidden                    :
  // :............................|___________|..............................................:

  const sliderWrapperRef = useRef<HTMLDivElement>(null);
  const sliderContentRef = useRef<HTMLDivElement>(null);

  const [distanceContentShiftedLeft, setDistanceContentShiftedLeft] =
    useState<number>(0);
  const [isHovered, setIsHovered] = useState<boolean>(false);

  const isEmpty = React.Children.count(children) === 0;

  // Ensure that the calculated offset always accounts for
  // the latest dimensions of the component.
  useEffect(() => {
    const resizeHandler = debounce(() => setDistanceContentShiftedLeft(0), 200);
    window.addEventListener('resize', resizeHandler);
    return () => window.removeEventListener('resize', resizeHandler);
  }, []);

  // Reset the shifted content whenever the resetScrollDep prop changes
  useEffect(() => {
    setDistanceContentShiftedLeft(0);
  }, [resetScrollDep]);

  const getRemainingContentSizeToRight = () => {
    const sliderWidth = sliderWrapperRef.current?.offsetWidth ?? 0;
    const contentWidth = sliderContentRef.current?.offsetWidth ?? 0;
    return contentWidth - sliderWidth - distanceContentShiftedLeft;
  };

  const handleLeftClicked = () => {
    const sliderWidth = sliderWrapperRef.current?.offsetWidth ?? 0;
    const scrollDistanceValue =
      typeof scrollDistance === 'number' ||
      typeof scrollDistance === 'undefined'
        ? scrollDistance
        : scrollDistance(distanceContentShiftedLeft);

    const distanceToScroll = scrollDistanceValue || sliderWidth;

    if (distanceContentShiftedLeft > distanceToScroll) {
      setDistanceContentShiftedLeft(
        distanceContentShiftedLeft - distanceToScroll,
      );
    } else {
      setDistanceContentShiftedLeft(0);
    }
  };

  const handleRightClicked = () => {
    const sliderWidth = sliderWrapperRef.current?.offsetWidth ?? 0;
    const scrollDistanceValue =
      typeof scrollDistance === 'number' ||
      typeof scrollDistance === 'undefined'
        ? scrollDistance
        : scrollDistance(distanceContentShiftedLeft);

    const distanceToScroll = scrollDistanceValue || sliderWidth;
    const remainingWidth = getRemainingContentSizeToRight();

    if (remainingWidth > 0) {
      setDistanceContentShiftedLeft(
        distanceContentShiftedLeft + distanceToScroll,
      );
    }
  };

  // We split the right side's gradient effect and arrow so that we can
  // always render the gradient when there's overflow, but only show the
  // arrow itself on hover. Note that the gradient is used in the block that
  // contains the arrow icon and is used to make the transition from content
  // to whitespace smooth.
  const shouldRenderRightGradient =
    !isEmpty && getRemainingContentSizeToRight() > 0;
  const shouldRenderRightArrow =
    shouldRenderRightGradient && (isHovered || alwaysShowArrows);
  const shouldRenderLeftGradientAndArrow =
    !isEmpty &&
    (isHovered || alwaysShowArrows) &&
    distanceContentShiftedLeft !== 0;

  return (
    <div
      className={className}
      onMouseEnter={(e) => {
        setIsHovered(true);
        onMouseEnter(e);
      }}
      onMouseLeave={(e) => {
        setIsHovered(false);
        onMouseLeave(e);
      }}
    >
      {header && <Header>{header}</Header>}
      <SliderWrapper
        ref={sliderWrapperRef}
        overflowHorizontally={overflowHorizontally ?? 0}
      >
        {shouldRenderLeftGradientAndArrow && (
          <ChevronLeft size={childSize} onClick={handleLeftClicked}>
            <Icon name='chevron-left' />
          </ChevronLeft>
        )}
        <SliderContent
          empty={isEmpty}
          style={{ transform: `translateX(-${distanceContentShiftedLeft}px)` }}
          ref={sliderContentRef}
        >
          {isEmpty ? emptyState : children}
        </SliderContent>
        {shouldRenderRightGradient && (
          <ChevronRight size={childSize} onClick={handleRightClicked}>
            {shouldRenderRightArrow && <Icon name='chevron-right' />}
          </ChevronRight>
        )}
      </SliderWrapper>
    </div>
  );
};

const Header = styled.div(
  ({ theme }) => `
  margin-bottom: ${theme.spacings[1]};
`,
);

const SliderWrapper = styled.div<{ overflowHorizontally: number }>(
  ({ overflowHorizontally }) => `
  /**
   * If a non-zero overflowHorizontally value is provided, it will
   * extend the width of the component by as many pixels to the
   * right. The styles below widen the component while repositioning
   * the children so that their final positions when scrolled all the
   * way left or right match the final positions they'd occupy had
   * we not widened the component in the first place.
   */
  width: calc(100% + ${overflowHorizontally}px);
  ${SliderContent} {
    padding-right: ${overflowHorizontally}px;
  }

  position: relative;
  overflow: hidden;
  white-space: nowrap; /* Ensures the slider's content do not break onto multiple lines */
`,
);

const SliderContent = styled.div<{ empty: boolean }>(
  ({ empty }) => `
  /* If the component is empty, use display:block so the emptyState can fill the horizontal space */
  display: ${empty ? 'block' : 'inline-block'};
  transition: transform 0.15s ease-in; /* The transform style linves inline on the component  */
`,
);

const Chevron = styled.button<{
  size: Size;
}>(
  ({ theme, size }) => `
  position: absolute;
  display: flex;
  align-items: center;
  top: 0;
  cursor: pointer;
  height: 100%;
  z-index: ${theme.zindex.sticky};
  border: none;
  outline: none;

  width: ${size === 'large' ? ARROW_WIDTH_LARGE : ARROW_WIDTH_SMALL}px;

  ${
    size === 'large'
      ? `
    .cu-icon {
      width: ${theme.spacings[5]};
      height: ${theme.spacings[5]};
    }
  `
      : ''
  };
`,
);

const ChevronRight = styled(Chevron)<{
  size: Size;
}>(
  ({ size }) => `
  right: 0;
  justify-content: ${size === 'large' ? `flex-end` : `center`};
  background: ${
    size === 'large'
      ? `linear-gradient(90deg,rgba(255,255,255,0) 0%,rgba(255,255,255,1) 60%,rgba(255,255,255,1) 100%)`
      : `linear-gradient(90deg,rgba(255,255,255,0) 0%,rgba(255,255,255,1) 40%,rgba(255,255,255,1) 100%)`
  };
`,
);

const ChevronLeft = styled(Chevron)<{
  size: Size;
}>(
  ({ size }) => `
  left: 0;
  justify-content: ${size === 'large' ? `flex-start` : `center`};
  background: ${
    size === 'large'
      ? `linear-gradient(270deg,rgba(255,255,255,0) 0%,rgba(255,255,255,1) 60%,rgba(255,255,255,1) 100%)`
      : `linear-gradient(270deg,rgba(255,255,255,0) 0%,rgba(255,255,255,1) 40%,rgba(255,255,255,1) 100%)`
  };
`,
);

export default HorizontalScrolling;
